import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// --- INTERFACES AND CONSTANTS ---

export interface CheckboxOption {
  id: string | number;
  name: string | number;
  selected: boolean;
}

export interface FilterOption {
  key: string;
  label: string;
  options: CheckboxOption[];
  selectedValues: (string | number)[];
  enabled: boolean;
}

export interface PeriodOption {
  value: string;
  viewValue: string;
}

export interface PromoDialogData {
  tableData: any[];
  appliedFilters?: { [key: string]: any };
  periodType: 'month' | 'week';
}

const KEY_LABELS: { [key: string]: string } = {
  retailer: 'Retailer',
  Manufacturer: 'Manufacturer',
  PriceSector: 'Price Sector',
  BrandHouse: 'Brand',
  BrandVariant: 'Sub Brand',
  BrandPackVariant: 'SKU Description',
  PackContents: 'Pack Size',
  LengthBand: 'Pack Size Bucket',
  'Lead Amount': 'Lead Amount',
};

const FILTER_ORDER = [
  'retailer',
  'Manufacturer',
  'PriceSector',
  'BrandHouse',
  'BrandVariant',
  'BrandPackVariant',
  'PackContents',
  'LengthBand',
  'Lead Amount',
];

@Component({
  selector: 'app-promo-filters-dialog',
  templateUrl: './promo-filters-dialog.component.html',
  styleUrls: ['./promo-filters-dialog.component.scss'],
})
export class PromoFiltersDialogComponent implements OnInit {
  public filters: FilterOption[] = [];
  public discountValue: number | null = null;
  private readonly rawTableData: any[] = [];

  public periodType: 'month' | 'week';
  public startPeriod: string | null = null;
  public endPeriod: string | null = null;
  public periodLabel: string = '';
  public allPeriodOptions: PeriodOption[] = [];
  public endPeriodOptions: PeriodOption[] = [];

  constructor(
    public dialogRef: MatDialogRef<PromoFiltersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PromoDialogData,
  ) {
    this.rawTableData = data.tableData || [];
    this.periodType = data.periodType;
    this.periodLabel = this.periodType === 'month' ? 'Month' : 'Week';

    const applied = data.appliedFilters;
    this.discountValue = applied?.['Discount'] || null;
    this.startPeriod = applied?.['startPeriod'] || null;
    this.endPeriod = applied?.['endPeriod'] || null;
  }

  ngOnInit() {
    this.extractPeriodOptions();
    this.populateFiltersFromData(this.rawTableData, this.data.appliedFilters);
    if (this.startPeriod) {
      this.onStartPeriodChange();
    }
  }

  private extractPeriodOptions(): void {
    if (!this.rawTableData.length) return;
    const firstRow = this.rawTableData[0];
    if (!firstRow.period_data) return;
    const periodKeys = new Set<string>();
    for (const year in firstRow.period_data) {
      if (Object.prototype.hasOwnProperty.call(firstRow.period_data, year)) {
        firstRow.period_data[year].forEach((monthObj: any) => {
          const key = Object.keys(monthObj)[0];
          if (key) periodKeys.add(key);
        });
      }
    }
    this.allPeriodOptions = Array.from(periodKeys)
      .sort()
      .map((key) => ({
        value: key,
        viewValue: this.formatPeriod(key),
      }));
    this.endPeriodOptions = this.allPeriodOptions;
  }

  private formatPeriod(key: string): string {
    if (this.periodType === 'month') {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const monthIndex = parseInt(key, 10) - 1;
      return monthNames[monthIndex] || key;
    }
    return `W'${key}`;
  }

  public onStartPeriodChange(): void {
    if (this.startPeriod) {
      this.endPeriodOptions = this.allPeriodOptions.filter(
        (opt) => opt.value >= this.startPeriod!,
      );
      if (this.endPeriod && this.endPeriod < this.startPeriod) {
        this.endPeriod = null;
      }
    } else {
      this.endPeriodOptions = this.allPeriodOptions;
    }
  }

  private populateFiltersFromData(
    sourceData: any[],
    appliedFilters?: { [key: string]: any },
  ): void {
    const hasAppliedFilters =
      appliedFilters && Object.keys(appliedFilters).length > 0;

    const initialFilters = FILTER_ORDER.map((key) => {
      const uniqueValues = [
        ...new Set(
          sourceData
            .map((row) => row[key])
            .filter((val) => val !== null && val !== undefined),
        ),
      ];

      // If filters are applied, use them. If not (initial load), select all.
      // onReset, appliedFilters is undefined, correctly defaulting to [].
      const selectedValues = hasAppliedFilters
        ? appliedFilters[key] || []
        : uniqueValues;

      return {
        key,
        label: KEY_LABELS[key] || key,
        options: uniqueValues.map((value) => ({
          id: value,
          name: value,
          selected: selectedValues.includes(value),
        })),
        selectedValues: selectedValues,
        enabled: true, // All filters are always enabled
      };
    });

    this.filters = this.getUpdatedFilterOptions(initialFilters);
  }

  onFilterApplied(
    event: { selectedFilters: any[]; event: string },
    changedFilterKey: string,
  ) {
    if (event.event !== 'apply') return;

    // Create a new state with the updated selections for the changed filter
    const nextFiltersState = this.filters.map((filter) =>
      filter.key === changedFilterKey
        ? { ...filter, selectedValues: event.selectedFilters }
        : filter,
    );

    // Recalculate options for all filters based on the new state
    this.filters = this.getUpdatedFilterOptions(nextFiltersState);
  }

  private getUpdatedFilterOptions(
    currentFilters: FilterOption[],
  ): FilterOption[] {
    const allActiveFilters = this.buildActiveFilterObject(
      false,
      currentFilters,
    );

    return currentFilters.map((filterToUpdate) => {
      // To get options for this filter, we check against all OTHER active filters
      const otherFilters = { ...allActiveFilters };
      delete otherFilters[filterToUpdate.key];

      const availableData = this.filterLocalData(
        this.rawTableData,
        otherFilters,
      );
      const availableOptions = new Set(
        availableData
          .map((row) => row[filterToUpdate.key])
          .filter((val) => val != null),
      );

      // Always include the current selections in the options list, even if they aren't in the available data
      const combinedOptions = new Set([
        ...filterToUpdate.selectedValues,
        ...availableOptions,
      ]);

      const newOptions = Array.from(combinedOptions)
        .sort()
        .map((value) => ({
          id: value,
          name: value,
          selected: filterToUpdate.selectedValues.includes(value),
        }));

      return {
        ...filterToUpdate,
        options: newOptions,
        enabled: true, // All filters are always enabled
      };
    });
  }

  onReset() {
    this.discountValue = null;
    this.startPeriod = null;
    this.endPeriod = null;
    this.onStartPeriodChange();
    // Resets to a state with no selections by passing no applied filters
    this.populateFiltersFromData(this.rawTableData);
  }

  onApply() {
    this.dialogRef.close(this.buildActiveFilterObject(true, this.filters));
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private buildActiveFilterObject(
    includeDiscount = false,
    sourceFilters: FilterOption[],
  ): { [key: string]: any } {
    const activeFilters: { [key: string]: any } = {};
    sourceFilters.forEach((filter) => {
      if (filter.selectedValues.length > 0) {
        activeFilters[filter.key] = filter.selectedValues;
      }
    });

    if (this.startPeriod) activeFilters['startPeriod'] = this.startPeriod;
    if (this.endPeriod) activeFilters['endPeriod'] = this.endPeriod;
    if (includeDiscount && this.discountValue !== null) {
      activeFilters['Discount'] = this.discountValue;
    }
    return activeFilters;
  }

  private filterLocalData(
    data: any[],
    filters: { [key: string]: any[] },
  ): any[] {
    const filterKeys = Object.keys(filters);
    if (filterKeys.length === 0) return data;

    return data.filter((row) => {
      return filterKeys.every((key) => {
        const filterValues = filters[key];
        if (!filterValues || filterValues.length === 0) return true;
        return filterValues.includes(row[key]);
      });
    });
  }

  trackByFilterKey(index: number, filter: FilterOption): string {
    return filter.key;
  }
}
